<template>
    <div :class="$style.tiles_wrapper">
        <div :class="[$style.tiles_button, $style.tabletPlus]">
            <button
                data-interface-target="open-links-menu"
                :class="$style.icon_wrapper"
                @click="toggleMore"
            >
                <Icon
                    name="plus"
                    :class="[$style.icon, showMore && $style.icon_rotate]"
                />
            </button>
        </div>

        <div
            :class="[
                $style.tiles_row,
                $style.desktop,
                hasRestItems && !showMore && $style.reduced_tiles,
                hasRestItems && showMore && $style.reduced_tiles_opened,
            ]"
        >
            <BaseLink
                v-for="(item, index) in allItems"
                :key="index"
                :to="item.to"
                :externalTo="item.externalTo"
                :target="item.target"
                :class="$style.tile"
            >
                <Typography
                    type="caption"
                    size="all-caps"
                    semibold
                >
                    {{ item.title }}
                </Typography>
            </BaseLink>
            <button
                :class="[$style.tile, $style.more_btn, !showMore && hasRestItems && $style.more_btn_visible]"
                @click="toggleMore"
            >
                <Typography
                    type="caption"
                    size="all-caps"
                    semibold
                >
                    {{ $tr('tiles.more') }}
                </Typography>
            </button>
        </div>
        <div
            ref="popupRef"
            :class="$style.popup"
        >
            <TransitionGroup
                tag="div"
                :class="$style.tiles_popup"
                :enterActiveClass="$style.fade_enter_active"
                :leaveToClass="$style.fade_leave_to"
            >
                <BaseLink
                    v-for="(item, index) in showMore ? allItems : []"
                    :key="index"
                    :to="item.to"
                    :externalTo="item.externalTo"
                    :target="item.target"
                    :class="$style.tile"
                >
                    <Typography
                        type="caption"
                        size="all-caps"
                        semibold
                        :class="$style.desktop"
                    >
                        {{ item.title }}
                    </Typography>
                    <Typography
                        type="header"
                        size="xs"
                        semibold
                        :class="$style.tabletPlus"
                    >
                        {{ item.title }}
                    </Typography>
                </BaseLink>
                <button
                    key="close-btn"
                    :class="[$style.tile, $style.close_btn, $style.desktop]"
                    @click="toggleMore"
                >
                    <Typography
                        type="caption"
                        size="all-caps"
                        accent
                    >
                        {{ $tr('tiles.close') }}
                    </Typography>
                </button>
            </TransitionGroup>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import BaseLink from '@/components/BaseLink.vue'
import Icon from '@/components/Icon/Icon.vue'
import Typography from '@/components/Typography.vue'
import { useTr } from '@/composables/use-tr'
import { ROUTE_NAMES } from '@/router/constants'
import {
    BLOG_PATH,
    BUSINESS_LANDPAGE_PATH,
    DEVELOPERS_LANDPAGE_PATH,
    type NavLink,
} from '@/router/navigation-constants'

const tr = useTr()

const allItems = computed((): NavLink[] => [
    {
        title: tr('tiles.for_business'),
        externalTo: BUSINESS_LANDPAGE_PATH,
        target: '_blank',
    },
    {
        title: tr('navigation.blog'),
        externalTo: BLOG_PATH,
        target: '_blank',
    },
    {
        title: tr('tiles.for_developers'),
        externalTo: DEVELOPERS_LANDPAGE_PATH,
        target: '_blank',
    },
    {
        title: tr('tiles.contacts'),
        to: { name: ROUTE_NAMES.CONTACTS },
    },
])

const popupRef = ref<HTMLElement | null>(null)
const buttonRef = ref<HTMLElement | null>(null)
const showMore = ref(false)
const hasRestItems = computed(() => allItems.value.length > 5)

function toggleShow(forceHide?: boolean) {
    if (forceHide === false) {
        showMore.value = false
        return
    }
    showMore.value = !showMore.value
}

function toggleMore(e: MouseEvent) {
    buttonRef.value = e.currentTarget as HTMLElement
    toggleShow()
}

function onClickOutside(event: MouseEvent) {
    if (!showMore.value) {
        return
    }
    const target = event.target as Node

    const clickedOutsidePopup = !popupRef.value?.contains(target)
    const clickedOutsideButton = !buttonRef.value?.contains(target)

    if (clickedOutsidePopup && clickedOutsideButton) {
        toggleShow(false)
    }
}

onMounted(() => {
    document.addEventListener('click', onClickOutside)
})

onBeforeUnmount(() => {
    document.removeEventListener('click', onClickOutside)
})
</script>

<style module>
.tiles_wrapper {
    position: relative;
    --tiles-gap: 2px;
}
.tiles_button {
    display: block;
}

.tiles_row {
    display: none;
}
.icon {
    color: black;
    transition: all 0.13s ease-in-out;
}
.icon_rotate {
    transform: rotate(-45deg);
}
.desktop {
    display: none;
}
.icon_wrapper:has(.icon_rotate) {
    background: conic-gradient(
        from 270deg at 50% 50%,
        #ff9c9c 0deg,
        #b099d0 113.39999914169312deg,
        #9ce52e 233.62456798553467deg,
        #ffc850 360deg
    );
}
.icon_wrapper {
    cursor: pointer;
    border-radius: 50%;
    height: 52px;
    padding: 14px;
    background: conic-gradient(
        from 90deg at 50% 50%,
        #ffc850 0deg,
        #9ce52e 126.37deg,
        #b099d0 246.6deg,
        #ff9c9c 360deg
    );
}
.tile {
    padding: 0 8px;
    border: none;
    border-radius: 8px;
    text-transform: uppercase;
    height: 25px;
    cursor: pointer;
    display: flex;
    align-items: center;
    white-space: nowrap;
    text-align: center;
    color: black;
    justify-content: flex-end;
}

.tile:hover,
.tile:focus {
    border-radius: 12px;
}
.popup {
    position: absolute;
    right: 0;
    padding: 8px 0;
    z-index: 1;
    display: flex;
    justify-content: flex-end;
    flex-direction: column;
    align-items: flex-end;
    gap: var(--tiles-gap);
}

.tile:first-child {
    background: #9ce52e;
}
.tile:nth-child(2) {
    background: var(--purple-600);
}
.tile:nth-child(3) {
    background: #ffc850;
}
.tile:nth-child(4),
.more_btn {
    background: #c3bce0;
}
.tile:nth-child(5) {
    background: #ffc3c3;
}
.tiles_popup {
    justify-content: flex-end;
    flex-direction: column;
    align-items: flex-end;
    display: flex;
    gap: 8px;
}

.close_btn {
    background: black;
    color: white;
    display: none;
}

.tiles_popup .tile {
    padding: 10px 16px;
    border-radius: 1000px;
    transition:
        transform 0.13s,
        opacity 0.13s;
}

.tiles_popup .tile:hover,
.tiles_popup .tile:focus {
    border-radius: 1000px;
    transform: scale(1.1);
}

.tiles_popup .tile:has(~ .tile:hover),
.tiles_popup .tile:has(~ .tile:has(:focus)) {
    transform: translateY(calc(var(--tiles-gap) * -1));
}

.tiles_popup .tile:hover ~ .tile,
.tiles_popup .tile:has(:focus) ~ .tile {
    transform: translateY(var(--tiles-gap));
}

.fade_enter_active {
    animation: bounce-in 0.4s ease-out;
}

.fade_leave_to {
    animation: bounce-out 0.4s ease-in;
}

@media (--desktop-medium-plus) {
    .tiles_wrapper {
        max-width: 220px;
    }

    .desktop {
        display: block;
    }

    .tabletPlus {
        display: none;
    }

    .popup {
        padding: var(--tiles-gap) 0;
    }
    .tiles_row {
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-end;
        gap: var(--tiles-gap);
    }
    .reduced_tiles > .tile:nth-child(n + 5) {
        display: none;
    }

    .reduced_tiles_opened > .tile:nth-child(n + 6) {
        display: none;
    }

    .tile.more_btn {
        display: none;
    }

    .tile.more_btn.more_btn_visible {
        display: block;
    }

    .tiles_popup {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        max-width: 150px;
        gap: var(--tiles-gap);
    }

    .tiles_popup .tile {
        padding: 0 8px;
        border-radius: 8px;
    }

    .tiles_popup .tile:hover,
    .tiles_popup .tile:focus,
    .tiles_popup .tile:has(~ .tile:hover),
    .tiles_popup .tile:has(~ .tile:has(:focus)),
    .tiles_popup .tile:hover ~ .tile,
    .tiles_popup .tile:has(:focus) ~ .tile {
        transform: none;
    }

    .tiles_popup .tile:hover,
    .tiles_popup .tile:focus {
        border-radius: 12px;
    }

    .tiles_popup > :nth-child(-n + 5) {
        display: none;
    }

    .fade_enter_active {
        animation: bounce-in 0.1s ease-out;
    }

    .fade_leave_to {
        animation: bounce-out 0.1s ease-in;
    }
}

@media (--desktop-large-plus) {
    .tiles_wrapper {
        width: 220px;
    }
}

/* animations */
@keyframes bounce-in {
    0% {
        transform: scale(0.7);
    }
    50% {
        transform: scale(1.3);
    }
    100% {
        transform: scale(1);
    }
}
@keyframes bounce-out {
    0% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.2);
    }
    100% {
        transform: scale(0.5);
    }
}
</style>
